import { Component, NgZone, OnInit, inject } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../guards/service/auth.service';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { DomainService } from '../../domain/domain.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  accountCheckForm!: UntypedFormGroup;
  loginForm!: UntypedFormGroup;

  userEmail = '';
  showSpaceSelector = false;
  availableSpaces: { space_id: number; name: string; avatar?: string | null }[] = [];
  selectedSpaceId: number | null = null;

  private router = inject(Router);
  private communityId: string | null = null;
  private slug: string | null = null;

  consultantRegistrationsData: any;
  providers: any;
  showPassword = false;
  userData: any;
  checkUserStep = true;
  userInSlugCommunity = true;
  loginData: any;
  blockingMessage: string | null = null;

  loadingCheck = false;
  loadingLogin = false;
  isSocial = false;

  constructor(
    private fb: UntypedFormBuilder,
    private authGuardService: AuthService,
    private dataService: FreelancerDataService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    public domainService: DomainService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.initializeAccountCheckForm();
    this.initializeLoginForm();
    this.getConsultantRegistrationDataList();

    const params = this.route.snapshot.params;
    this.slug = params['slug'] || null;
    this.communityId = params['communityId'] || null;

    if (this.slug) localStorage.setItem('slug', this.slug);
    if (this.communityId) localStorage.setItem('communityId', this.communityId);
  }

  initializeAccountCheckForm(): void {
    this.accountCheckForm = this.fb.group({
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
    });
  }

  initializeLoginForm(): void {
    this.loginForm = this.fb.group({
      password: new UntypedFormControl('', [Validators.required]),
      space_id: new UntypedFormControl(null),
    });
  }

  submitAccountCheck(): void {
    if (this.loadingCheck || this.accountCheckForm.invalid) return;

    this.loadingCheck = true;
    this.blockingMessage = null;
    this.showPassword = false;
    this.showSpaceSelector = false;
    this.userInSlugCommunity = true;
    this.isSocial = false;

    this.userEmail = String(this.accountCheckForm.value.email || '').trim();

    const providerId = this.getProviderIdByName('KontactApp') ?? 5;
    sessionStorage.setItem('providerId', providerId.toString());

    const checkParams = { email: this.userEmail };

    this.authGuardService.checkAccount(checkParams).subscribe({
      next: (res) => {
        this.loadingCheck = false;

        const userData = res?.data;
        this.userData = userData;

        const hasJobseeker =
          !!this.userData &&
          Array.isArray(this.userData.Jobseekers) &&
          this.userData.Jobseekers.length > 0;

        const hasRecruiter =
          !!this.userData &&
          Array.isArray(this.userData.Recruiters) &&
          this.userData.Recruiters.length > 0;

        const hasAnyAccount = hasJobseeker || hasRecruiter;

        if (!hasAnyAccount) {
          this.goToRegistration();
          return;
        }

        this.isSocial = userData?.isSocial === true;

        if (this.isSocial) {
          this.goToForgotPasswordForSocialUser();
          return;
        }

        this.checkUserStep = false;

        if (this.domainService.isCommunityDomain()) {
          const communityId = this.communityId ? Number(this.communityId) : null;

          const isInCommunity =
            Array.isArray(userData?.CommunityUsers) && communityId !== null
              ? userData.CommunityUsers.some(
                  (cu: { community_id: number }) =>
                    Number(cu.community_id) === Number(communityId)
                )
              : false;

          if (isInCommunity) {
            this.showPassword = true;
            this.userInSlugCommunity = true;
            this.toastrService.info(
              'Please input existing password to continue to the Community.'
            );
          } else {
            this.showPassword = true;
            this.userInSlugCommunity = false;
            this.toastrService.info(
              'Please input existing password to continue Community Registration.'
            );
          }

          return;
        }

        if (this.domainService.isProfileDomain()) {
          if (hasJobseeker) {
            this.availableSpaces = userData.Jobseekers.map(
              (j: {
                space_id: number;
                Spaces?: { name: string; avatar?: string | null };
              }) => ({
                space_id: j.space_id,
                name: j.Spaces?.name,
                avatar: j.Spaces?.avatar || null,
              })
            );

            this.showSpaceSelector = true;
            this.showPassword = true;
            this.toastrService.info(
              'Please select a space and input password to continue.'
            );
          } else if (hasRecruiter) {
            this.availableSpaces = [];
            this.showSpaceSelector = false;
            this.showPassword = true;
            this.toastrService.info('Please input existing password to continue.');
          }
        }
      },
      error: () => {
        this.loadingCheck = false;
        this.goToRegistration();
      },
    });
  }

  private goToRegistration(): void {
    const regPath =
      this.slug && this.communityId
        ? `/${this.slug}/${this.communityId}/registration/${this.userEmail}`
        : `/registration/${this.userEmail}`;

    this.ngZone.run(() => this.router.navigateByUrl(regPath));
  }

  private goToForgotPasswordForSocialUser(): void {
    const returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') || undefined;

    const queryParams = {
      email: this.userEmail,
      message:
        'You previously used social logins on KontactApp.  Please setup a password to continue in the new system.',
      ...(returnUrl ? { returnUrl } : {}),
    };

    this.ngZone.run(() => {
      if (this.domainService.isCommunityDomain() && this.slug && this.communityId) {
        this.router.navigate(
          [`/${this.slug}/${this.communityId}/forgot-password`],
          { queryParams }
        );
      } else {
        this.router.navigate(['/forgot-password'], { queryParams });
      }
    });
  }

  continueLoginProfile(): void {
    if (this.loadingLogin) return;

    const email = this.userEmail;
    const password = this.loginForm.value.password;
    const spaceID = Number(this.loginForm.value.space_id);

    if (!password) {
      this.toastrService.error('Please enter password.');
      return;
    }

    if (!spaceID) {
      this.toastrService.error('Please select a space.');
      return;
    }

    localStorage.setItem('spaceID', String(spaceID));
    this.loginUser(email, password, spaceID);
  }

  continueLoginCommunity(): void {
    if (this.loadingLogin) return;

    const email = this.userEmail;
    const password = this.loginForm.value.password;

    if (!password) {
      this.toastrService.error('Please enter password.');
      return;
    }

    localStorage.removeItem('spaceID');

    this.loginUser(email, password);
  }

  loginUser(email: string, password: string, selectedSpaceID?: number): void {
    this.loadingLogin = true;

    if (this.domainService.isCommunityDomain()) {
      this.loginData = {
        email,
        password,
        provider_id: 5,
        community_id: this.communityId,
      };

      localStorage.removeItem('spaceID');
    } else {
      const spaceID =
        selectedSpaceID ||
        Number(this.loginForm.value.space_id || localStorage.getItem('spaceID') || 0);

      this.loginData = {
        email,
        password,
        provider_id: 5,
        space_id: spaceID,
      };

      if (spaceID) {
        localStorage.setItem('spaceID', String(spaceID));
      }
    }

    this.authGuardService.login(this.loginData).subscribe({
      next: () => {
        this.loadingLogin = false;

        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

        if (returnUrl) {
          this.ngZone.run(() =>
            this.router.navigateByUrl(returnUrl, {
              state: { closeSidebar: true },
            })
          );
          return;
        }

        if (this.domainService.isCommunityDomain()) {
          const slug = localStorage.getItem('slug');
          const communityId = localStorage.getItem('communityId');

          this.ngZone.run(() => {
            if (slug && communityId) {
              this.router.navigate([`/${slug}/${communityId}/home`]);
            } else {
              this.router.navigate(['/home']);
            }
          });
        } else {
          this.ngZone.run(() => this.router.navigate(['/profile']));
        }
      },
      error: (err) => {
        this.loadingLogin = false;
        this.handleLoginError(err);
      },
    });
  }

  onSpaceSelected(): void {
    if (this.selectedSpaceId) {
      this.loginForm.patchValue({ space_id: this.selectedSpaceId });
    }
  }

  continueExistingUserCommunityRegistration(): void {
    if (this.loadingLogin) return;

    const email = this.userEmail;
    const password = this.loginForm.value.password;

    if (!password) {
      this.toastrService.error('Please enter password.');
      return;
    }

    this.loadingLogin = true;
    sessionStorage.setItem('regHandoff', '1');

    const loginData = { email, password, provider_id: 5 };

    this.authGuardService.login(loginData).subscribe({
      next: () => {
        this.loadingLogin = false;

        sessionStorage.removeItem('regHandoff');
        sessionStorage.setItem('tempPassword', password);

        localStorage.removeItem('token');
        localStorage.removeItem('spaceID');

        const regPath = `/${this.slug}/${this.communityId}/registration/${this.userEmail}`;
        this.ngZone.run(() =>
          this.router.navigateByUrl(regPath, { replaceUrl: true })
        );
      },
      error: (error: HttpErrorResponse) => {
        this.loadingLogin = false;
        sessionStorage.removeItem('regHandoff');

        let errorMessage = 'An unexpected error occurred. Please try again.';
        if (error.error?.description) errorMessage = error.error.description;
        if (error.error?.error?.description) {
          errorMessage = error.error.error.description;
        }

        this.toastrService.error(errorMessage, 'Login Failed');
      },
    });
  }

  getConsultantRegistrationDataList(): void {
    this.dataService.getconsultantRegistration().subscribe({
      next: (res) => {
        this.consultantRegistrationsData = res.data;
        this.providers = this.consultantRegistrationsData?.providers;
      },
      error: (err) =>
        console.error('Error fetching consultant registration:', err),
    });
  }

  forgotPassword(): void {
    const returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') || undefined;

    this.ngZone.run(() => {
      if (this.domainService.isCommunityDomain() && this.slug && this.communityId) {
        this.router.navigate(
          [`/${this.slug}/${this.communityId}/forgot-password`],
          {
            queryParams: returnUrl ? { returnUrl } : undefined,
          }
        );
      } else {
        this.router.navigate(['/forgot-password'], {
          queryParams: returnUrl ? { returnUrl } : undefined,
        });
      }
    });
  }

 

  getProviderIdByName(providerName: string): number | null {
    const provider = this.providers?.find(
      (p: any) => p.name?.toLowerCase() === providerName.toLowerCase()
    );

    return provider?.provider_id || null;
  }

  private handleLoginError(error: any): void {
    const apiDescription =
      error?.error?.error?.description ||
      error?.error?.description ||
      '';

    if (
      this.domainService.isCommunityDomain() &&
      apiDescription === 'Your account has been suspended.'
    ) {
      this.blockingMessage =
        'Your account has been suspended. Contact us at info@kontactapp.com to re-activate.';
      return;
    }

    this.toastrService.error(apiDescription || 'Login failed', 'Error');
  }

  blockedAccountBack(): void {
    this.blockingMessage = null;
    this.checkUserStep = true;
    this.showPassword = false;
    this.showSpaceSelector = false;

    const returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') || undefined;

    if (this.domainService.isCommunityDomain() && this.slug && this.communityId) {
      this.router.navigate([`/${this.slug}/${this.communityId}/login`], {
        queryParams: returnUrl ? { returnUrl } : undefined,
      });
    } else {
      this.router.navigate(['/login'], {
        queryParams: returnUrl ? { returnUrl } : undefined,
      });
    }
  }
}